import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Truck, Plus, PencilSimple, CaretRight, ClockCounterClockwise, Copy, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const D = (x) => (isNaN(parseFloat(x)) ? 0 : parseFloat(x));
const EMPTY = {
  name: "", registration: "TBC", vehicle_type: "", payload_kg: "", pallet_capacity: "", home_depot: "", toll_class: "1", is_third_party: false,
  profile: {
    effective_from: new Date().toISOString().slice(0, 10),
    rental_finance: "0", insurance: "0", tracker_licence: "0", driver_employment: "0", overhead_allocation: "0",
    diesel_price_per_litre: "22.40", fuel_consumption_km_l: "0", tyres_per_km: "0", maintenance_per_km: "0", other_variable_per_km: "0",
  },
};

export default function Vehicles() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [vehicles, setVehicles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [workingDays, setWorkingDays] = useState(22);
  const [tab, setTab] = useState("own"); // own | third

  const load = useCallback(async () => {
    const { data } = await api.get("/vehicles");
    setVehicles(data);
    setSelectedId((cur) => cur || data[0]?.id || null);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings").then((r) => setWorkingDays(Number(r.data.working_days_per_month) || 22)); }, []);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    api.get(`/vehicles/${selectedId}`).then((r) => setDetail(r.data));
  }, [selectedId, vehicles]);

  const openNew = () => { setForm({ ...EMPTY, is_third_party: tab === "third" }); setEditing(false); setOpen(true); };
  const openEdit = () => {
    if (!detail) return;
    const p = detail.current_profile || {};
    setForm({
      name: detail.name, registration: detail.registration || "TBC", vehicle_type: detail.vehicle_type || "",
      payload_kg: detail.payload_kg || "", pallet_capacity: detail.pallet_capacity || "", home_depot: detail.home_depot || "", toll_class: detail.toll_class || "1", is_third_party: !!detail.is_third_party,
      profile: {
        effective_from: new Date().toISOString().slice(0, 10),
        rental_finance: p.rental_finance || "0", insurance: p.insurance || "0", tracker_licence: p.tracker_licence || "0",
        driver_employment: p.driver_employment || "0", overhead_allocation: p.overhead_allocation || "0",
        diesel_price_per_litre: p.diesel_price_per_litre || "22.40", fuel_consumption_km_l: p.fuel_consumption_km_l || "0",
        tyres_per_km: p.tyres_per_km || "0", maintenance_per_km: p.maintenance_per_km || "0", other_variable_per_km: p.other_variable_per_km || "0",
      },
    });
    setEditing(true); setOpen(true);
  };

  const openDuplicate = () => {
    if (!detail) return;
    const p = detail.current_profile || {};
    setForm({
      name: `${detail.name} (copy)`, registration: "TBC", vehicle_type: detail.vehicle_type || "",
      payload_kg: detail.payload_kg || "", pallet_capacity: detail.pallet_capacity || "", home_depot: detail.home_depot || "", toll_class: detail.toll_class || "1", is_third_party: !!detail.is_third_party,
      profile: {
        effective_from: new Date().toISOString().slice(0, 10),
        rental_finance: p.rental_finance || "0", insurance: p.insurance || "0", tracker_licence: p.tracker_licence || "0",
        driver_employment: p.driver_employment || "0", overhead_allocation: p.overhead_allocation || "0",
        diesel_price_per_litre: p.diesel_price_per_litre || "22.40", fuel_consumption_km_l: p.fuel_consumption_km_l || "0",
        tyres_per_km: p.tyres_per_km || "0", maintenance_per_km: p.maintenance_per_km || "0", other_variable_per_km: p.other_variable_per_km || "0",
      },
    });
    setEditing(false); setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/vehicles/${selectedId}`, {
          name: form.name, registration: form.registration, vehicle_type: form.vehicle_type,
          payload_kg: form.payload_kg, pallet_capacity: form.pallet_capacity, home_depot: form.home_depot, toll_class: form.toll_class, is_third_party: form.is_third_party,
        });
        await api.post(`/vehicles/${selectedId}/profiles`, form.profile);
        toast.success("Vehicle profile updated (new cost version saved)");
      } else {
        const { data } = await api.post("/vehicles", form);
        setSelectedId(data.id);
        toast.success("Vehicle created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async () => {
    if (!detail) return;
    if (!window.confirm(`Delete ${detail.name}? This removes the vehicle and its cost profiles. Existing quotes keep their saved snapshot.`)) return;
    try {
      await api.delete(`/vehicles/${selectedId}`);
      toast.success("Vehicle deleted");
      setSelectedId(null); setDetail(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const p = detail?.current_profile || {};
  const shownVehicles = vehicles.filter((v) => (tab === "third" ? v.is_third_party : !v.is_third_party));
  const totalFixed = D(p.rental_finance) + D(p.insurance) + D(p.tracker_licence) + D(p.driver_employment) + D(p.overhead_allocation);
  const dailyFixed = workingDays > 0 ? totalFixed / workingDays : 0;
  const fp = form.profile;
  const totalFixedForm = D(fp.rental_finance) + D(fp.insurance) + D(fp.tracker_licence) + D(fp.driver_employment) + D(fp.overhead_allocation);
  const dailyFixedForm = workingDays > 0 ? totalFixedForm / workingDays : 0;
  const varTotalForm = D(fp.tyres_per_km) + D(fp.maintenance_per_km) + D(fp.other_variable_per_km) + (D(fp.fuel_consumption_km_l) > 0 ? D(fp.diesel_price_per_litre) / D(fp.fuel_consumption_km_l) : 0);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-600 mb-1">Fleet cost profiles</div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate-900">Vehicle settings</h1>
          <p className="text-sm text-slate-500 mt-1">Maintain the operating assumptions that power every quote.</p>
        </div>
        {isAdmin && (
          <Button data-testid="new-vehicle-btn" onClick={openNew} className="bg-teal-600 hover:bg-teal-700 text-white">
            <Plus size={18} weight="bold" className="mr-2" />{tab === "third" ? "Add 3rd-party vehicle" : "Add vehicle"}
          </Button>
        )}
      </div>

      {/* Own fleet / 3rd party tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-slate-200" data-testid="vehicle-tabs">
        {[["own", "Own fleet"], ["third", "3rd party"]].map(([t, label]) => (
          <button key={t} onClick={() => { setTab(t); setSelectedId(null); }} data-testid={`vehicle-tab-${t}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t ? "border-teal-600 text-teal-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Fleet list */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden h-max">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-900">{tab === "third" ? "3rd-party vehicles" : "Active fleet"}</h3>
            <span className="text-xs text-slate-400">{shownVehicles.length} vehicles</span>
          </div>
          <div className="p-2">
            {shownVehicles.map((v) => (
              <button key={v.id} onClick={() => setSelectedId(v.id)} data-testid={`fleet-item-${v.id}`}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-md text-left transition-colors ${selectedId === v.id ? "bg-teal-50 ring-1 ring-teal-200" : "hover:bg-slate-50"}`}>
                <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0"><Truck size={20} className="text-slate-500" weight="fill" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 text-sm truncate">{v.name}</div>
                  <div className="text-[11px] text-slate-400 truncate">{v.is_third_party ? "3rd party · supplier hire" : `${v.vehicle_type || "—"} · ${v.payload_kg ? `${Number(v.payload_kg).toLocaleString("en-ZA")} kg` : "—"}`}</div>
                </div>
                <CaretRight size={16} className="text-slate-300 shrink-0" />
              </button>
            ))}
            {shownVehicles.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-400">{tab === "third" ? "No 3rd-party vehicles yet. Add one to use on the External quote tab." : "No vehicles yet."}</div>}
          </div>
        </div>

        {/* Detail */}
        {detail && (
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="vehicle-detail">
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider bg-teal-50 text-teal-700 border border-teal-200 mb-2">
                  {detail.active ? "Active" : "Inactive"}
                </span>
                {detail.is_third_party && <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 mb-2 ml-2" data-testid="third-party-badge">3rd party</span>}
                <h2 className="font-heading font-bold text-2xl text-slate-900 tracking-tight">{detail.name}</h2>
                <p className="text-sm text-slate-500">{detail.vehicle_type || "—"} · Registration {detail.registration || "TBC"}</p>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={openDuplicate} data-testid="duplicate-vehicle-btn"><Copy size={16} className="mr-1.5" />Duplicate</Button>
                  <Button variant="outline" onClick={openEdit} data-testid="edit-profile-btn"><PencilSimple size={16} className="mr-1.5" />Edit profile</Button>
                  <Button variant="outline" onClick={del} data-testid="delete-vehicle-btn" className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"><Trash size={16} className="mr-1.5" />Delete</Button>
                </div>
              )}
            </div>

            <Grid cells={[
              ["Vehicle name", detail.name],
              ["Registration", detail.registration || "TBC"],
              ["Vehicle type", detail.vehicle_type || "—"],
              ["Payload", detail.payload_kg ? `${Number(detail.payload_kg).toLocaleString("en-ZA")} kg` : "—"],
              ["Pallet capacity", detail.pallet_capacity || "—"],
              ["Home depot", detail.home_depot || "—"],
              ["Toll class", `Class ${detail.toll_class || "1"}`],
            ]} />

            <SectionTitle>Fixed monthly costs</SectionTitle>
            {detail.is_third_party ? (
              <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-sm text-amber-800 mb-2" data-testid="third-party-note">3rd-party vehicle — no operating cost profile. Costed per quote via the supplier's hire figure on the External tab.</div>
            ) : (<>
            <Grid cells={[
              ["Rental / finance", fmtMoney(p.rental_finance)],
              ["Insurance", fmtMoney(p.insurance)],
              ["Tracker & licence", fmtMoney(p.tracker_licence)],
              ["Driver employment", fmtMoney(p.driver_employment)],
              ["License renewal", fmtMoney(p.overhead_allocation)],
              ["Total fixed / month", fmtMoney(totalFixed), true],
              [`Fixed daily / trip (÷ ${workingDays})`, fmtMoney(dailyFixed), true],
            ]} />

            <SectionTitle>Variable operating costs</SectionTitle>
            <Grid cells={[
              ["Diesel price / litre", fmtMoney(p.diesel_price_per_litre)],
              ["Fuel consumption km/l", String(p.fuel_consumption_km_l || "—")],
              ["Tyres / km", fmtMoney(p.tyres_per_km)],
              ["Maintenance & repairs / km", fmtMoney(p.maintenance_per_km)],
              ["Other variable / km", fmtMoney(p.other_variable_per_km)],
              ["Variable cost / km total", fmtMoney(p.calculated_variable_per_km), true],
            ]} />
            </>)}

            <div className="text-xs text-slate-400 flex items-center gap-1 mt-4">
              <ClockCounterClockwise size={14} /> Cost profile effective from {fmtDate(p.effective_from)} · {(detail.profiles?.length || 1)} version(s) on record
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto cd-scroll">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit vehicle" : (form.is_third_party ? "Add 3rd-party vehicle" : "Add vehicle")}</DialogTitle></DialogHeader>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Identity</div>
          {form.is_third_party ? (
            <div className="grid grid-cols-1 gap-2">
              <Fld label="Vehicle description"><input data-testid="vehicle-name" className="cd-input" placeholder="e.g. 8t Tail Lift · Line Haul · 34t Superlink" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Fld>
              <p className="text-xs text-slate-400">3rd-party vehicle — no operating cost. It's costed per quote via the supplier's hire figure on the External tab.</p>
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Vehicle name"><input data-testid="vehicle-name" className="cd-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Fld>
            <Fld label="Registration"><input className="cd-input" value={form.registration} onChange={(e) => setForm({ ...form, registration: e.target.value })} /></Fld>
            <Fld label="Vehicle type"><input className="cd-input" placeholder="e.g. 10 ton curtainside" value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} /></Fld>
            <Fld label="Payload (kg)"><input className="cd-input data-num" value={form.payload_kg} onChange={(e) => setForm({ ...form, payload_kg: e.target.value })} /></Fld>
            <Fld label="Pallet capacity"><input className="cd-input" placeholder="e.g. 14 pallets" value={form.pallet_capacity} onChange={(e) => setForm({ ...form, pallet_capacity: e.target.value })} /></Fld>
            <Fld label="Home depot"><input className="cd-input" placeholder="e.g. Pomona, JHB" value={form.home_depot} onChange={(e) => setForm({ ...form, home_depot: e.target.value })} /></Fld>
            <Fld label="Toll class (SANRAL)">
              <select className="cd-input" value={form.toll_class} onChange={(e) => setForm({ ...form, toll_class: e.target.value })} data-testid="vehicle-toll-class">
                {["1", "2", "3", "4"].map((c) => <option key={c} value={c}>Class {c}</option>)}
              </select>
            </Fld>
          </div>
          )}

          {!form.is_third_party && (<>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-2">Fixed monthly costs</div>
          <div className="grid grid-cols-2 gap-3">
            <PFld label="Rental / finance" k="rental_finance" form={form} setForm={setForm} />
            <PFld label="Insurance" k="insurance" form={form} setForm={setForm} />
            <PFld label="Tracker & licence" k="tracker_licence" form={form} setForm={setForm} />
            <PFld label="Driver employment" k="driver_employment" form={form} setForm={setForm} />
            <PFld label="License renewal" k="overhead_allocation" form={form} setForm={setForm} />
            <div className="flex items-end">
              <div className="w-full bg-teal-50 border border-teal-200 rounded-md px-3 py-2 flex items-center justify-between gap-2">
                <div><div className="text-[11px] uppercase tracking-wider text-teal-600">Total fixed / month</div><div className="data-num font-semibold text-teal-700">{fmtMoney(totalFixedForm)}</div></div>
                <div className="text-right"><div className="text-[11px] uppercase tracking-wider text-teal-600">Daily / trip (÷ {workingDays})</div><div className="data-num font-semibold text-teal-700" data-testid="dialog-daily-fixed">{fmtMoney(dailyFixedForm)}</div></div>
              </div>
            </div>
          </div>

          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-2">Variable operating costs</div>
          <div className="grid grid-cols-2 gap-3">
            <PFld label="Diesel price / litre" k="diesel_price_per_litre" form={form} setForm={setForm} />
            <PFld label="Fuel consumption km/l" k="fuel_consumption_km_l" form={form} setForm={setForm} />
            <PFld label="Tyres / km" k="tyres_per_km" form={form} setForm={setForm} />
            <PFld label="Maintenance & repairs / km" k="maintenance_per_km" form={form} setForm={setForm} />
            <PFld label="Other variable / km" k="other_variable_per_km" form={form} setForm={setForm} />
            <Fld label="Effective from"><input type="date" className="cd-input" value={form.profile.effective_from} onChange={(e) => setForm({ ...form, profile: { ...form.profile, effective_from: e.target.value } })} /></Fld>
            <div className="flex items-end col-span-2"><div className="w-full bg-teal-50 border border-teal-200 rounded-md px-3 py-2 flex items-center justify-between"><div className="text-[11px] uppercase tracking-wider text-teal-600">Variable cost / km total <span className="text-teal-500 normal-case">(incl. fuel {D(fp.fuel_consumption_km_l) > 0 ? fmtMoney(D(fp.diesel_price_per_litre) / D(fp.fuel_consumption_km_l)) : "R 0.00"}/km)</span></div><div className="data-num font-semibold text-teal-700 text-lg" data-testid="dialog-var-total">{fmtMoney(varTotalForm)}</div></div></div>
          </div>
          </>)}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="vehicle-save" onClick={save} className="bg-teal-600 hover:bg-teal-700 text-white">{editing ? "Save profile" : "Create vehicle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Grid({ cells }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 border border-slate-200 rounded-md overflow-hidden mb-2">
      {cells.map(([label, value, highlight], i) => (
        <div key={i} className={`p-3 border-b border-r border-slate-100 ${highlight ? "bg-teal-50" : ""}`}>
          <div className="text-[11px] text-slate-400">{label}</div>
          <div className={`text-sm font-medium ${highlight ? "text-teal-700 data-num" : "text-slate-900"}`}>{value}</div>
        </div>
      ))}
    </div>
  );
}
function SectionTitle({ children }) { return <div className="text-sm font-heading font-semibold text-slate-700 mt-5 mb-2">{children}</div>; }
function Fld({ label, children }) { return <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label><div className="mt-1">{children}</div></div>; }
function PFld({ label, k, form, setForm }) {
  return <Fld label={label}><input className="cd-input data-num" value={form.profile[k]} onChange={(e) => setForm({ ...form, profile: { ...form.profile, [k]: e.target.value } })} data-testid={`profile-${k}`} /></Fld>;
}
